export interface ExtractionResult {
  text: string;
  sourceType: "txt" | "pdf" | "docx" | "image";
}

function extToSourceType(name: string): ExtractionResult["sourceType"] | null {
  const ext = name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "txt":
      return "txt";
    case "pdf":
      return "pdf";
    case "docx":
      return "docx";
    case "png":
    case "jpg":
    case "jpeg":
      return "image";
    default:
      return null;
  }
}

/**
 * Extracts text from an uploaded file, dispatching to the right client-side
 * extractor. Heavy extractors (pdf.js, mammoth, tesseract.js) are dynamically
 * imported so the base app bundle stays small.
 */
export async function extractFromFile(
  file: File,
  onProgress?: (message: string) => void
): Promise<ExtractionResult> {
  const sourceType = extToSourceType(file.name);
  if (!sourceType) {
    throw new Error(`Unsupported file type: ${file.name}`);
  }

  switch (sourceType) {
    case "txt": {
      const text = await file.text();
      if (!text.trim()) throw new Error("This file is empty.");
      return { text, sourceType };
    }

    case "pdf": {
      onProgress?.("Opening PDF...");
      const { extractPdfText } = await import("./extractors/pdf");
      const text = await extractPdfText(file, onProgress);
      return { text, sourceType };
    }

    case "docx": {
      onProgress?.("Reading document...");
      const { extractDocxText } = await import("./extractors/docx");
      const text = await extractDocxText(file);
      return { text, sourceType };
    }

    case "image": {
      onProgress?.("Running OCR...");
      const { recognizeImage } = await import("./extractors/ocr");
      const text = await recognizeImage(file, (pct) => onProgress?.(`Running OCR... ${pct}%`));
      if (!text) throw new Error("Couldn't find any text in this image.");
      return { text, sourceType };
    }
  }
}
